import { X, Check, Trash2, Plus, CalendarCheck2, Globe, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EventForm } from "./EventForm";
import { Attachments } from "./Attachments";
import { TravelWarning } from "./TravelWarning";
import { colourOf, fmtTime, REPEAT_LABELS } from "./palette";

export const DayPanel = ({
  day, events, reminders, holiday, accent, clock, homeZone, travel, dryWindows,
  form, setForm, onSave, editingId, onEdit, onDeleteEvent,
  ideas, ideasLoading, onIdeas, onClose,
  draft, setDraft, onAddReminder, onTickReminder, onDeleteReminder,
  uploading, onUpload, onOpenFile, onDeleteFile,
}) => (
  <div className="mt-4 rounded-2xl border border-white/10 bg-panel p-5" data-testid="day-panel">
    <div className="flex flex-wrap items-center gap-2">
      <p className="font-display text-sm font-bold tracking-tight">{day}</p>
      {holiday && (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-400/40 bg-violet-400/10 px-3 py-1 text-xs font-semibold text-violet-200"
              data-testid="day-panel-holiday">
          <CalendarCheck2 className="h-3 w-3" /> {holiday}
        </span>
      )}
      <button
        data-testid="day-panel-close"
        onClick={onClose}
        aria-label="Close this day"
        className="ms-auto rounded-full border border-white/10 p-1 text-ink-soft hover:text-ink"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>

    <TravelWarning warnings={travel} clock={clock} />

    <div className="mt-3 space-y-3" data-testid="day-events">
      {events.map((ev) => (
        <div key={`${ev.event_id}-${ev.day}`}>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colourOf(ev.colour) }} />
            <span dir="auto" className="font-semibold">{ev.title}</span>
            <span className="text-xs text-ink-soft">
              {ev.all_day ? "All day" : `${fmtTime(ev.start, clock)}${ev.end ? `–${fmtTime(ev.end, clock)}` : ""}`}
              {ev.location ? ` · ${ev.location}` : ""}
              {ev.repeat !== "none" ? ` · ${REPEAT_LABELS[ev.repeat]}` : ""}
              {ev.notes ? ` · ${ev.notes}` : ""}
            </span>
            {ev.tz && ev.tz !== homeZone && (
              <span className="inline-flex items-center gap-1 text-xs text-ink-soft"
                    data-testid={`event-zone-${ev.event_id}`}>
                <Globe className="h-3 w-3" /> {ev.tz.split("/").pop().replace(/_/g, " ")}
                {ev.local_start ? ` · ${fmtTime(ev.local_start, clock)} your time` : ""}
              </span>
            )}
            {ev.shared_from && (
              <span className="inline-flex items-center gap-1 text-xs text-ink-soft">
                <Users className="h-3 w-3" /> {ev.shared_from}
              </span>
            )}
            <button
              data-testid={`event-edit-${ev.event_id}`}
              onClick={() => onEdit(ev)}
              className="ms-auto rounded-full border border-white/10 px-3 py-1 text-xs text-ink-soft hover:text-ink"
            >
              Edit
            </button>
            <button
              data-testid={`event-delete-${ev.event_id}`}
              onClick={() => onDeleteEvent(ev)}
              aria-label="Delete event"
              className="rounded-full border border-white/10 p-1 text-ink-soft hover:border-destructive/60 hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <Attachments
            event={ev}
            accent={accent}
            busy={uploading === ev.event_id}
            onUpload={onUpload}
            onOpen={onOpenFile}
            onDelete={onDeleteFile}
          />
        </div>
      ))}
    </div>

    <EventForm
      form={form}
      setForm={setForm}
      onSave={onSave}
      editing={Boolean(editingId)}
      ideas={ideas}
      ideasLoading={ideasLoading}
      onIdeas={onIdeas}
      accent={accent}
      homeZone={homeZone}
      dryWindows={dryWindows}
    />

    <div className="mt-3 space-y-2">
      {reminders.map((r) => (
        <div key={r.reminder_id} className="flex flex-wrap items-center gap-2 text-sm">
          <button
            data-testid={`day-reminder-tick-${r.reminder_id}`}
            onClick={() => onTickReminder(r)}
            aria-label={r.done ? "Mark as not done" : "Mark as done"}
            className={`rounded-full border p-1 transition-colors ${
              r.done ? "border-forest bg-forest/15 text-forest" : "border-white/20 text-ink-soft hover:text-ink"
            }`}
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <span dir="auto" className={r.done ? "text-ink-soft line-through" : ""}>{r.text}</span>
          <button
            data-testid={`day-reminder-delete-${r.reminder_id}`}
            onClick={() => onDeleteReminder(r)}
            aria-label="Delete reminder"
            className="ms-auto rounded-full border border-white/10 p-1 text-ink-soft hover:border-destructive/60 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>

    <div className="mt-3 flex flex-wrap gap-2">
      <input
        data-testid="day-reminder-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onAddReminder()}
        maxLength={200}
        placeholder="e.g. chase the landlord about the boiler"
        className="min-h-[44px] flex-1 rounded-xl border border-white/10 bg-page px-4 text-sm outline-none placeholder:text-ink-soft"
      />
      <Button
        data-testid="day-reminder-add"
        onClick={onAddReminder}
        className="min-h-[44px] rounded-full px-5 text-sm text-page active:scale-95"
        style={{ backgroundColor: accent }}
      >
        <Plus className="me-1.5 h-4 w-4" /> Remind me
      </Button>
    </div>
  </div>
);
